/**
 * Submits the Request Quote form to HubSpot's public Forms API
 * (`api.hsforms.com/submissions/v3/integration/submit/...`). Used as a
 * fallback path when the V4 developer embed is blocked (ad blocker, CSP,
 * corporate firewall) — the embed loader and the Forms API are served from
 * different domains, so a block on one doesn't necessarily affect the other.
 *
 * No auth header — the portal+form IDs in the URL ARE the routing key.
 * HubSpot validates submissions server-side; an unrecognized form returns 4xx.
 *
 * Note: the Forms API uses bare field names (`firstname`, `email`, ...) where
 * the V4 embed uses the `0-1/` prefix. Same contact-record fields under the
 * hood — just two different submission surfaces.
 */

import { HUBSPOT_PORTAL_ID, HUBSPOT_FORM_ID } from '../config/hubSpot';

const FORMS_API_URL =
  `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_ID}`;

export interface QuoteSubmission {
  designName: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  company: string;
  city: string;
  country: string;
  /** Equipment-list message assembled by buildQuoteMessage */
  message: string;
}

const toApiFields = (data: QuoteSubmission) =>
  [
    { name: 'design_tool_project_name', value: data.designName },
    { name: 'firstname', value: data.firstName },
    { name: 'lastname', value: data.lastName },
    { name: 'email', value: data.email },
    { name: 'phone', value: data.phoneNumber },
    { name: 'company', value: data.company },
    { name: 'city', value: data.city },
    { name: 'country_dropdown', value: data.country },
    { name: 'message', value: data.message },
  ].filter(field => field.value);

export const submitQuoteToHubSpotFormsApi = async (
  data: QuoteSubmission,
): Promise<void> => {
  const response = await fetch(FORMS_API_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      submittedAt: Date.now(),
      fields: toApiFields(data),
      context: {
        pageUri: window.location.href,
        pageName: 'Workplace Designer - Request Quote',
      },
    }),
  });

  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(
      `HubSpot Forms API submission failed (${response.status}): ${detail}`,
    );
  }
};
